// src/store/modules/agentChat/mutations.js
import Vue from 'vue';
import formatAssistantMessage from '../utils/messageFormatter';

function appendThinkingStep(message, thinkingStatus) {
  if (!thinkingStatus) return;

  if (!Array.isArray(message.thinkingSteps)) {
    Vue.set(message, 'thinkingSteps', []);
  }

  if (!message.thinkingSteps.includes(thinkingStatus)) {
    message.thinkingSteps.push(thinkingStatus);
  }

  message.thinkingStatus = thinkingStatus;
}

export default {
  SET_SOURCE_REQUEST(state, { siteId, requestId }) {
    state.sourcesSiteId = siteId;
    state.sourcesRequestId = requestId;
  },
  SET_DEFAULT_SOURCE(state, source) { state.defaultSource = source; },
  SET_SELECTED_SOURCE(state, source) { state.selectedSource = source; },
  SET_SOURCE_AVAILABILITY(state, available) { state.genieAvailable = available; },
  SET_CHAT_AGENTS(state, agents) { state.chatAgents = agents; },
  SET_SOURCES_LOADING(state, loading) { state.sourcesLoading = loading; },
  SET_LAST_ASSISTANT_STATUS(state, { conversationId, status }) {
    const message = [...(state.messagesByConversationId[conversationId] || [])].reverse().find((m) => m.role === 'assistant');
    if (message) Vue.set(message, 'status', status);
  },
  SET_ACTIVE_REQUEST(state, { requestId = null, backend = null }) {
    state.activeRequestId = requestId;
    if (backend) state.activeBackend = backend;
  },
  SET_ANALYTICS(state, { conversationId, messageId, data }) {
    const message = (state.messagesByConversationId[conversationId] || []).find((m) => m.id === messageId);
    if (!message) return;
    message.rawText = data.text || '';
    message.renderedHtml = formatAssistantMessage(message.rawText, 'markdown');
    Vue.set(message, 'blocks', Array.isArray(data.blocks) ? data.blocks : []);
    Vue.set(message, 'status', data.status || 'in_progress');
    message.toolPending = false;
  },
  SET_ACTIVE_CONVERSATION_ID(state, conversationId) {
    state.activeConversationId = conversationId;
  },

  SET_STREAMING(state, value) {
    state.isStreaming = value;
  },

  SET_ABORT_CONTROLLER(state, controller) {
    state.abortController = controller;
  },

  SET_ERROR(state, value) {
    state.error = value;
  },

  SET_LOADING_CONVERSATIONS(state, value) {
    state.isLoadingConversations = value;
  },

  SET_LOADING_ACTIVE_CONVERSATION(state, value) {
    state.isLoadingActiveConversation = value;
  },

  SET_CONVERSATIONS(state, conversations) {
    const localConversations = state.conversations.filter((conversation) => (
      conversation.id && conversation.id.startsWith('local-')
    ));

    state.conversations = [
      ...localConversations,
      ...conversations.filter((conversation) => (
        !localConversations.some((local) => local.id === conversation.id)
      )),
    ];

    conversations.forEach((conversation) => {
      if (!state.messagesByConversationId[conversation.id]) {
        Vue.set(state.messagesByConversationId, conversation.id, []);
      }
    });
  },

  ENSURE_CONVERSATION(state, conversation) {
    const exists = state.conversations.some((c) => c.id === conversation.id);

    if (!exists) {
      state.conversations.unshift({
        id: conversation.id,
        source: conversation.source || state.selectedSource,
        title: conversation.title || 'New chat',
        createdAt: conversation.createdAt || new Date().toISOString(),
        updatedAt: conversation.updatedAt || new Date().toISOString(),
      });
    }

    if (!state.messagesByConversationId[conversation.id]) {
      Vue.set(state.messagesByConversationId, conversation.id, []);
    }
  },

  SET_CONVERSATION_MESSAGES(state, { conversation, messages }) {
    const existing = state.conversations.find((c) => c.id === conversation.id);

    if (existing) {
      Vue.set(existing, 'source', conversation.source || 'knowledge-base');
      Vue.set(existing, 'agentDisplayName', conversation.agentDisplayName);
      existing.title = conversation.title || existing.title;
      existing.createdAt = conversation.createdAt || existing.createdAt;
      existing.updatedAt = conversation.updatedAt || existing.updatedAt;
    } else {
      state.conversations.unshift(conversation);
    }

    Vue.set(state.messagesByConversationId, conversation.id, messages);
    Vue.set(state.loadedConversationIds, conversation.id, true);
  },

  TOUCH_CONVERSATION(state, conversationId) {
    const existing = state.conversations.find((c) => c.id === conversationId);
    if (!existing) return;

    existing.updatedAt = new Date().toISOString();

    state.conversations = [
      existing,
      ...state.conversations.filter((c) => c.id !== conversationId),
    ];
  },

  SET_CONVERSATION_TITLE(state, { conversationId, title }) {
    const existing = state.conversations.find((c) => c.id === conversationId);
    if (existing && title) {
      existing.title = title;
    }
  },

  ADD_MESSAGE(state, { conversationId, message }) {
    if (!state.messagesByConversationId[conversationId]) {
      Vue.set(state.messagesByConversationId, conversationId, []);
    }

    state.messagesByConversationId[conversationId].push(message);
  },

  REPLACE_CONVERSATION_ID(state, { oldId, newId }) {
    if (oldId === newId) return;

    const oldMessages = state.messagesByConversationId[oldId] || [];
    const newMessages = state.messagesByConversationId[newId] || [];

    if (!state.messagesByConversationId[newId]) {
      Vue.set(state.messagesByConversationId, newId, oldMessages);
    } else if (!newMessages.length && oldMessages.length) {
      Vue.set(state.messagesByConversationId, newId, oldMessages);
    }

    if (state.messagesByConversationId[oldId]) {
      Vue.delete(state.messagesByConversationId, oldId);
    }

    if (state.loadedConversationIds[oldId]) {
      Vue.set(state.loadedConversationIds, newId, true);
      Vue.delete(state.loadedConversationIds, oldId);
    }

    state.conversations = state.conversations.map((conversation) => {
      if (conversation.id !== oldId) return conversation;
      return {
        ...conversation,
        id: newId,
      };
    });

    if (state.activeConversationId === oldId) {
      state.activeConversationId = newId;
    }
  },

  APPEND_ASSISTANT_DELTA(state, {
    conversationId,
    messageId,
    text,
    format,
  }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);

    if (!message) return;

    if (format) {
      message.format = format;
    }

    message.rawText += text || '';
    message.renderedHtml = formatAssistantMessage(message.rawText, message.format);
  },

  SET_ASSISTANT_MESSAGE(state, {
    conversationId,
    messageId,
    text,
    format,
  }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);

    if (!message) return;

    if (format) {
      message.format = format;
    }

    message.rawText = text || '';
    message.renderedHtml = formatAssistantMessage(message.rawText, message.format);
  },

  SET_ASSISTANT_FORMAT(state, {
    conversationId,
    messageId,
    format,
  }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);

    if (!message) return;

    message.format = format || 'text';
    message.renderedHtml = formatAssistantMessage(message.rawText || '', message.format);
  },

  SET_ASSISTANT_TYPE(state, {
    conversationId,
    messageId,
    type,
  }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);

    if (!message) return;

    message.type = type || 'plain_text';
  },

  SET_ASSISTANT_TOOL_PENDING(state, {
    conversationId,
    messageId,
    toolName,
    thinkingStatus,
  }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);
    if (!message) return;
    message.toolPending = true;
    message.activeToolName = toolName || null;
    appendThinkingStep(message, thinkingStatus || 'Working through the request');
  },

  SET_ASSISTANT_THINKING_STATUS(state, {
    conversationId,
    messageId,
    thinkingStatus,
  }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);
    if (!message) return;
    appendThinkingStep(message, thinkingStatus);
  },

  SET_ASSISTANT_PAYLOAD(state, {
    conversationId,
    messageId,
    payloadType,
    payload,
    thinkingStatus,
  }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);

    if (!message) return;

    message.type = payloadType || 'plain_text';
    message.payload = payload || null;
    // Payload has arrived - clear the pending tool state.
    message.toolPending = false;
    message.activeToolName = null;
    appendThinkingStep(message, thinkingStatus);
  },

  MARK_MESSAGE_DONE(state, { conversationId, messageId }) {
    const messages = state.messagesByConversationId[conversationId] || [];
    const message = messages.find((m) => m.id === messageId);

    if (message) {
      message.done = true;
      message.toolPending = false;
      message.thinkingStatus = null;
    }
  },
};
